<script>
import moment from 'moment'
import { eventTypeDisplayNames } from './EventTypes'
import ValueComboBox from '../campaigns/ValueComboBox'
import { removeEmptyFields } from '../utils/removeEmptyFields'

export default {
  name: 'MobilizeV2EventsEdit',
  components: {
    ValueComboBox
  },
  props: {
    event: { type: [Object, null], required: false },
    eventOverrides: { type: [Object, null], required: false }
  },
  computed: {
    eventTypes() {
      return Object.entries(eventTypeDisplayNames).map(([value, text]) => ({ text, value }))
    },
    virtualOptions() {
      return ['Yes', 'No']
    },
    propsOverrides() {
      return (
        (this.$props.eventOverrides && this.$props.eventOverrides.data && this.$props.eventOverrides.data.overrides) ||
        {}
      )
    },
    eventData() {
      return (this.$props.event && this.$props.event.data) || {}
    },
    eventStartDate() {
      return this.eventData.start_date && moment.unix(this.eventData.start_date).format('YYYY-MM-DD')
    },
    eventEndDate() {
      return this.eventData.end_date && moment.unix(this.eventData.end_date).format('YYYY-MM-DD')
    }
  },
  data() {
    const propsOverrides =
      (this.$props.eventOverrides && this.$props.eventOverrides.data && this.$props.eventOverrides.data.overrides) || {}
    const defaultFormData = {
      title: propsOverrides.title || null,
      description: propsOverrides.description || null,
      summary: propsOverrides.summary || null,
      is_virtual: propsOverrides.is_virtual || null,
      featured_image_url: propsOverrides.featured_image_url || null,
      browser_url: propsOverrides.browser_url || null,
      timezone: propsOverrides.timezone || null,
      event_type: null,
      location: {
        locality: (propsOverrides.location && propsOverrides.location.locality) || null,
        region: (propsOverrides.location && propsOverrides.location.region) || null
      },
      start_date: (propsOverrides.start_date && moment.unix(propsOverrides.start_date).format('YYYY-MM-DD')) || null,
      end_date: (propsOverrides.end_date && moment.unix(propsOverrides.end_date).format('YYYY-MM-DD')) || null
    }
    return {
      form: {
        data: defaultFormData
      }
    }
  },
  methods: {
    moment,
    removeEventType(eventType) {
      this.form.data.eventType.splice(this.form.data.eventType.indexOf(eventType), 1)
      this.form.data.eventType = [...this.form.data.eventType]
    },
    onSubmitEditsClick() {
      this.$emit('submit-edits', removeEmptyFields(this.form.data) || {})
    }
  }
}
</script>

<template>
  <div class="mobilizev2-view">
    <div class="header-wrapper">
      <h2>Edit Event</h2>
      <v-btn color="primary" class="close-dialog mini-hidden" @click="$emit('close-dialog')">
        <v-icon left>close</v-icon>
        Cancel
      </v-btn>
    </div>
    <v-alert outlined color="grey">
      Any fields that are specified will be overwritten. Leave fields empty to use the value from the original event
      data.
    </v-alert>
    <v-form class="form">
      <v-text-field
        label="Title"
        placeholder="Title"
        v-model="form.data.title"
        prepend-icon="mdi-text"
        :hint="eventData.title"
        persistent-hint
      />
      <v-text-field
        label="Description"
        placeholder="Description"
        v-model="form.data.description"
        prepend-icon="mdi-text"
        :hint="eventData.description"
        persistent-hint
      />
      <v-text-field
        label="Summary"
        placeholder="Summary"
        v-model="form.data.summary"
        prepend-icon="mdi-text"
        :hint="eventData.summary"
        persistent-hint
      />
      <v-select
        attach
        label="Virtual"
        :items="virtualOptions"
        v-model="form.data.is_virtual"
        prepend-icon="mdi-laptop"
        :hint="eventData.is_virtual ? 'Yes' : 'No'"
        persistent-hint
      />
      <v-text-field
        label="City"
        placeholder="City"
        v-model="form.data.location.locality"
        prepend-icon="mdi-map-marker"
        :hint="eventData.location && eventData.location.locality"
        persistent-hint
      />
      <v-text-field
        label="State"
        placeholder="State"
        v-model="form.data.location.region"
        prepend-icon="mdi-map-marker"
        :hint="eventData.location && eventData.location.region"
        persistent-hint
      />
      <v-text-field
        label="Timezone"
        placeholder="Timezone"
        v-model="form.data.timezone"
        prepend-icon="mdi-clock"
        :hint="eventData.timezone"
        persistent-hint
      />
      <v-text-field
        label="Featured Image URL"
        placeholder="Featured Image URL"
        v-model="form.data.featured_image_url"
        prepend-icon="mdi-link"
        :hint="eventData.featured_image_url"
        persistent-hint
      />
      <v-text-field
        label="Browser URL"
        placeholder="Browser URL"
        v-model="form.data.browser_url"
        prepend-icon="mdi-link"
        :hint="eventData.browser_url"
        persistent-hint
      />
      <value-combo-box
        v-model="form.data.event_type"
        :items="eventTypes"
        chips
        clearable
        label="Event Type"
        prepend-icon="mdi-text"
        attach
        :hint="eventData.event_type"
        persistent-hint
      >
        <template v-slot:selection="{ attrs, item, select, selected }">
          <v-chip v-bind="attrs" :input-value="selected" close @click="select" @click:close="removeEventType(item)">
            {{ item.text }}&nbsp;
          </v-chip>
        </template>
      </value-combo-box>
      <v-text-field
        label="Start date"
        :hint="eventStartDate"
        persistent-hint
        v-model="form.data.start_date"
        prepend-icon="mdi-calendar"
      />
      <v-text-field
        label="End date"
        :hint="eventEndDate"
        persistent-hint
        v-model="form.data.end_date"
        prepend-icon="mdi-calendar"
      />
      <div class="bottom-button-wrapper">
        <v-btn color="primary" class="apply-filter mini-hidden" @click="onSubmitEditsClick">
          <v-icon left>edit</v-icon>
          Submit edits
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<style lang="scss" scoped>
.mobilizev2-view {
  background: white;
  margin-top: 70px;
  min-height: 120vh;
  height: auto;
  width: calc(100vw - 250px);
}

.header-wrapper {
  display: flex;
  justify-content: space-between;

  h2 {
    margin-bottom: 20px;
  }
}

.date-picker {
  position: absolute;
  z-index: 1;
  margin-top: 20px;
}

.bottom-button-wrapper {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
</style>
